import { createPortal } from "react-dom";

import { InteractionObjectType } from "../../../../constants";
import { UploadDocumentModal } from "../../../../route/shared/projects/project/documents/components/UploadDocumentModal";
import { GetProjectIssuancesResponse, SearchIssuanceTransactionsResponse } from "../../../../service/query";
import { TagsPanel } from "../../../TagsPanel";
import { ReleaseToInventoryModal } from "../AddIssuanceTransactionPanel/components";
import { ProgressTransactionPanel } from "./ProgressTransactionPanel";
import { useProgressTransactionPanel } from "./useProgressTransactionPanel";

interface ProgressIssuanceTransactionPanelProps {
  // The transaction being progressed — the panel is opened from its row in the transactions table.
  issuanceTransaction: SearchIssuanceTransactionsResponse["results"][0];
  /*
    The issuance the transaction belongs to. Absent on the org-wide inventory management board, which opens
    the transactions table from a filter — the settlement check then falls to the server.
  */
  issuance?: GetProjectIssuancesResponse["results"][0];
  isBiodiversity: boolean;
  variant: "hub" | "seed";
  show: boolean;
  onClose: () => void;
  handleRefresh: () => void;
}

/*
  Progressing a transaction into its follow-on (BR-02364): a quote is taken up or cancelled, a reservation
  completes or falls through, a sale is reversed. Wires the presentational panel to its data.
*/
export const ProgressIssuanceTransactionPanel = ({
  issuanceTransaction,
  issuance,
  isBiodiversity,
  variant,
  show,
  onClose,
  handleRefresh,
}: ProgressIssuanceTransactionPanelProps): JSX.Element => {
  const {
    panelData,
    setPanelData,
    typeData,
    settlementWarning,
    settlementWarningTitle,
    isFormLocked,
    progressionNotice,
    errors,
    isHandleSubmitLoading,
    handleClosePanel,
    handleSubmit,
    showReleaseToInventoryModal,
    releaseToInventoryType,
    showTagsPanel,
    setShowTagsPanel,
    changedTagsExternal,
    setChangedTagsExternal,
    setRefreshTags,
    tagPills,
    documents,
    canManageAttachments,
    canEdit,
    projectUuid,
    showUploadDocumentModal,
    handleAddAttachment,
    handleCloseUploadDocumentModal,
    handleDocumentsUploaded,
    handleRemoveAttachment,
    handleDownloadAttachment,
  } = useProgressTransactionPanel({ show, issuanceTransaction, issuance, onClose, handleRefresh });

  // A partial commitment on a reservation or sale asks whether the remainder is released; a quote never does.
  if (releaseToInventoryType) {
    return (
      <ReleaseToInventoryModal
        type={releaseToInventoryType}
        show={showReleaseToInventoryModal}
        onClose={handleClosePanel}
        handleRefresh={handleRefresh}
        parentIssuanceTransaction={issuanceTransaction}
        issuanceTransactionPanelData={panelData}
      />
    );
  }

  return (
    <ProgressTransactionPanel
      show={show}
      panelData={panelData}
      setPanelData={setPanelData}
      typeData={typeData}
      isBiodiversity={isBiodiversity}
      habitatType={issuanceTransaction.habitatGroup}
      variant={variant}
      tagPills={tagPills}
      documents={documents}
      settlementWarning={settlementWarning}
      settlementWarningTitle={settlementWarningTitle}
      isFormLocked={isFormLocked}
      progressionNotice={progressionNotice}
      errors={errors}
      attachmentsReadOnly={!canManageAttachments}
      isReadOnly={!canEdit}
      isSubmitLoading={isHandleSubmitLoading}
      onAddTag={() => setShowTagsPanel(true)}
      onAddAttachment={handleAddAttachment}
      onRemoveAttachment={handleRemoveAttachment}
      onDownloadAttachment={handleDownloadAttachment}
      onSubmit={handleSubmit}
      onClose={handleClosePanel}
      overlayChildren={
        <>
          <TagsPanel
            objectUuid={undefined}
            objectType={InteractionObjectType.ISSUANCE_TRANSACTION}
            show={showTagsPanel}
            onClose={() => setShowTagsPanel(false)}
            shouldRefreshTags={() => setRefreshTags((prev) => !prev)}
            changedTagsExternal={changedTagsExternal}
            setChangedTagsExternal={setChangedTagsExternal}
          />
          {/* Uploads a project document, which handleDocumentsUploaded then links to the new transaction. */}
          {/*
            Portalled to the body because the flyout panel carries a transform, which makes it the containing
            block for position: fixed descendants. Left inside it, the modal centres on the narrow panel rather
            than the viewport and its right-hand side is cut off past the edge of the screen.
          */}
          {createPortal(
            <UploadDocumentModal
              show={showUploadDocumentModal}
              closeModal={handleCloseUploadDocumentModal}
              projectUuid={projectUuid}
              refreshDocuments={false}
              setRefreshDocuments={() => undefined}
              onUploaded={handleDocumentsUploaded}
              className="Modal_aboveFlyoutPanel"
            />,
            document.body
          )}
        </>
      }
    />
  );
};
